import { init as initDash0 } from '@dash0/sdk-web';
import { consumeImpersonationFragment } from 'korint-sdk';

/**
 * Must run before `initDash0`: the browser's `PerformanceNavigationTiming` entry
 * for a `/impersonate#impersonationToken=…` load captures the fragment as an
 * immutable snapshot, which Dash0's navigation instrumentation then reports
 * verbatim. Redirecting first means Dash0 never sees this load.
 */
const isImpersonationHandoff = consumeImpersonationFragment();

const dash0EndpointUrl = import.meta.env.VITE_DASH0_ENDPOINT_URL;
const dash0WebsiteToken = import.meta.env.VITE_DASH0_WEBSITE_TOKEN;

if (
  !isImpersonationHandoff &&
  import.meta.env.VITE_DASH0_ENABLED === 'true' &&
  dash0EndpointUrl &&
  dash0WebsiteToken
) {
  initDash0({
    serviceName: 'garantie-plus',
    endpoint: { url: dash0EndpointUrl, authToken: dash0WebsiteToken },
    propagators: [
      {
        type: 'traceparent',
        match: [
          /^https?:\/\/localhost(:\d+)?(\/|$)/,
          /^https?:\/\/([a-z0-9-]+\.)*korint\.io(:\d+)?(\/|$)/,
        ],
      },
    ],
  });
}

import { defaultNS, Toaster, TooltipProvider, useAssertedParams, useToast } from '@korint/folio';
import { applyEnvPrefixToDocumentTitle } from '@folio/utils';
import { KorintConfigProvider } from 'korint-sdk/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { clientOptions } from './client';
import korintConfig from './korint.config';
import { router } from './routes';

import './index.css';

import { initI18n } from '@korint/folio';
import tenantFrTranslationsOverride from './locales/fr/translations.json';

initI18n({
  fr: {
    [defaultNS]: tenantFrTranslationsOverride,
  },
});

applyEnvPrefixToDocumentTitle();

export function App() {
  const toast = useToast();

  return (
    <KorintConfigProvider
      korintConfig={korintConfig}
      clientOptions={clientOptions}
      config={{
        useAutomaticParams: useAssertedParams,
        defaultErrorHandler: error => {
          if ('userError' in error) {
            // Kubb hooks errors
            if (typeof error.userError === 'string') {
              const errorWithTypesFixed = error as unknown as {
                userError: string;
                userHelp: string;
              };
              toast.toast({
                title: errorWithTypesFixed.userError ?? 'Erreur',
                description: errorWithTypesFixed.userHelp,
              });
            }
            // Korint SDK errors
            if (typeof error.userError === 'object') {
              toast.toast({
                title: error.userError.userError ?? 'Erreur',
                description: error.userError.userHelp,
              });
            }
          }
        },
      }}
    >
      <Toaster />
      <TooltipProvider>
        <RouterProvider router={router} />
      </TooltipProvider>
    </KorintConfigProvider>
  );
}

if (!isImpersonationHandoff) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
